import { supabase } from '@/lib/supabase';
import type { Profile, TablesUpdate } from '@/types';
import type { DmAudience, ProfileVisibility, SettingsDraft } from '../settings';

/* ===========================================================
   Settings persistence.

   The V2 `profiles` table only has columns for a SUBSET of what
   the OG settings drafted: display_name, handle, avatar_url, bio,
   status, email_digest, push_permission. (Theme/`skin_id` is owned
   by the Locker + avatar rail, not Settings — see `useThemeSelection`.)
   The OG also wrote notif_dm/reply/mention/live + priv_* — those columns
   don't exist in the V2 schema, and CLAUDE.md forbids ad-hoc
   migrations. So, exactly like the OG's own localStorage fallback
   (`winr-settings`), those extras persist client-side here until a
   real schema lands. DB-backed fields go to Supabase.
   =========================================================== */

const EXTRAS_KEY = 'winr-settings';

/** Prefs with no `profiles` column yet — persisted to localStorage. */
export interface SettingsExtras {
  notifDM: boolean;
  notifReply: boolean;
  notifMention: boolean;
  notifLive: boolean;
  privShowOnline: boolean;
  privDMs: DmAudience;
  privProfile: ProfileVisibility;
}

export function loadSettingsExtras(): Partial<SettingsExtras> {
  try {
    const raw = localStorage.getItem(EXTRAS_KEY);
    return raw ? (JSON.parse(raw) as Partial<SettingsExtras>) : {};
  } catch {
    return {};
  }
}

export function saveSettingsExtras(extras: SettingsExtras): void {
  try {
    localStorage.setItem(EXTRAS_KEY, JSON.stringify(extras));
  } catch {
    /* private mode / quota — non-fatal, the DB fields still saved */
  }
}

/**
 * Persist a settings draft. DB-backed fields patch the `profiles` row; the
 * notification/privacy extras go to localStorage. Mirrors OG `saveDraft`,
 * including its "omit empties" behavior for the optional text fields.
 */
export async function saveProfileSettings(id: string, draft: SettingsDraft): Promise<Profile> {
  const patch: TablesUpdate<'profiles'> = {
    display_name: draft.displayName || undefined,
    handle: draft.handle ? `@${draft.handle}` : undefined,
    avatar_url: draft.avatarUrl || undefined,
    bio: draft.bio || undefined,
    status: draft.status || undefined,
    email_digest: draft.notifDigest,
    push_permission: draft.notifPush ? 'granted' : 'default',
  };
  // Drop undefined keys so we never null out an unchanged column (OG parity).
  (Object.keys(patch) as (keyof typeof patch)[]).forEach(
    (k) => patch[k] === undefined && delete patch[k],
  );

  const { data, error } = await supabase
    .from('profiles')
    .update(patch)
    .eq('id', id)
    .select('*')
    .single();
  if (error) throw error;

  saveSettingsExtras({
    notifDM: draft.notifDM,
    notifReply: draft.notifReply,
    notifMention: draft.notifMention,
    notifLive: draft.notifLive,
    privShowOnline: draft.privShowOnline,
    privDMs: draft.privDMs,
    privProfile: draft.privProfile,
  });

  return data;
}

/**
 * Is `@handle` free? Looks for any OTHER profile already holding it. Powers the
 * live "Available / Taken" status under the username field (OG `handleTaken`).
 */
export async function isHandleAvailable(handle: string, selfId: string): Promise<boolean> {
  const { data, error } = await supabase
    .from('profiles')
    .select('id')
    .eq('handle', `@${handle}`)
    .neq('id', selfId)
    .maybeSingle();
  if (error) throw error;
  return !data;
}
